import BaseCheckbox from '@components/BaseCheckbox';
import {
    Badge,
    Box,
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Theme,
    Tooltip,
    Typography,
} from '@mui/material';
import { getCellStyles, getColumnStyles } from '@pages/camerasOverview/configs';
import { getColumnAlignment } from '@pages/camerasOverview/configs/columnConfigs';
import { TableInfoContent } from '@pages/overview/hook/ReportsAnalyzingCriteriaAndPcnProvider';
import color from '@theme/Colors';
import React, { CSSProperties } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { formatNumber } from 'src/helpers';
import { DataTypes, SortCriterion, SortMode } from 'src/helpers/sortColumTable';
import { CellOfRow } from '.';
import { SkeletonRow } from './TableSkeleton';

interface BaseTableProps<T> {
    loading: boolean;
    data: T[];
    tableHeaders: Array<TableInfoContent<keyof T>>;
    orderBy: SortCriterion<T>[];
    isMultiSort?: boolean;
    stickyLeftFields: Array<keyof T>;
    stickyRightFields?: Array<keyof T>;
    alignmentFields?: { [key in keyof T]?: React.CSSProperties['textAlign'] };
    alignmentDataFields?: { [key in keyof T]?: 'inherit' | 'center' | 'left' | 'justify' | 'right' | undefined };
    tableDataTypes: { [key in keyof T]: DataTypes };
    noDataComponent: React.ReactNode;
    excludedColumns: Array<{ id: keyof T }>;
    totals?: Record<string, number>;
    monitoredFields?: Array<keyof T>;
    rateFields?: Array<keyof T>;
    getFormattedRateValue?: (id: keyof T) => string;
    handleRequestSort: (property: keyof T) => void;
    renderColumnData: (id: keyof T, rowData: T, index: number) => React.ReactNode;
    selectedCamera?: {
        handleSelected: (checked: boolean, mac?: string) => void;
        keySelected: keyof T;
        selectedAll: boolean;
        macCameras: string[];
    };
    handleContextMenu?: (event: React.MouseEvent<HTMLSpanElement>, property: keyof T) => void;
}

export const BaseTable = <T,>({
    loading,
    data,
    tableHeaders,
    orderBy,
    isMultiSort,
    handleRequestSort,
    renderColumnData,
    stickyLeftFields,
    stickyRightFields,
    alignmentFields,
    alignmentDataFields,
    tableDataTypes,
    noDataComponent,
    excludedColumns,
    totals,
    monitoredFields,
    getFormattedRateValue,
    rateFields,
    selectedCamera,
    handleContextMenu,
}: BaseTableProps<T>) => {
    return (
        <TableContainer
            component={Box}
            sx={{
                '& .chart-scroll-bar': {
                    '&::-webkit-scrollbar': { height: '5px' },
                    '&::-webkit-scrollbar-thumb:hover': {
                        cursor: 'pointer',
                        backgroundColor: color.grey600,
                        borderRadius: '3px',
                    },
                    '::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar:vertical': { display: 'none' },
                },
            }}
        >
            <ScrollContainer
                className="container chart-scroll-bar"
                style={{
                    height: '100%',
                    width: '100%',
                    paddingBottom: '5px',
                    position: 'relative',
                    maxHeight: 700,
                    overflowX: 'auto',
                }}
                hideScrollbars={!data?.length ? true : false}
            >
                <Table aria-label="simple table">
                    {data?.length > 0 && (
                        <TableHead sx={{ ...tableHeadStyles({}), maxWidth: 'max-content', whiteSpace: 'nowrap' }}>
                            <TableRow>
                                {selectedCamera && (
                                    <TableCell
                                        sx={{
                                            backgroundColor: '#f0f0f0',
                                            ...cellSelectedSxProps,
                                        }}
                                    >
                                        <BaseCheckbox
                                            onChange={(e, checked) => {
                                                selectedCamera?.handleSelected(checked);
                                            }}
                                            checked={selectedCamera.selectedAll}
                                        />
                                    </TableCell>
                                )}

                                {tableHeaders.map((headCell, index) => {
                                    const cellStyles = getCellStyles(
                                        headCell.id,
                                        tableDataTypes,
                                        stickyLeftFields,
                                        alignmentFields ?? {},
                                        stickyRightFields,
                                        !!selectedCamera
                                    );

                                    const totalValue = totals?.[headCell.id as string] ?? 0;

                                    const isRateFieldResult = rateFields?.includes(headCell.id as keyof T);

                                    const formattedValue = isRateFieldResult
                                        ? getFormattedRateValue?.(headCell.id as keyof T)
                                        : formatNumber(totalValue);

                                    const isMonitoredField = monitoredFields?.includes(headCell.id as keyof T);

                                    const findIndexOrderBy = orderBy.findIndex((item) => item.property === headCell.id);
                                    const findOrderBy = orderBy.find((item) => item.property === headCell.id);

                                    return (
                                        <TableCell
                                            key={index}
                                            align={cellStyles.align}
                                            sx={{
                                                ...cellStyles.sx,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            <Tooltip
                                                title={
                                                    isMonitoredField
                                                        ? `${headCell.tooltipHover} (${formattedValue}${
                                                              isRateFieldResult ? '%' : ''
                                                          })`
                                                        : headCell.tooltipHover
                                                }
                                                placement="top"
                                            >
                                                <span>
                                                    {excludedColumns.some((col) => col.id === headCell.id) ? (
                                                        <Typography
                                                            fontSize={14}
                                                            fontWeight="medium"
                                                            whiteSpace={'pre-wrap'}
                                                        >
                                                            {headCell.label}{' '}
                                                            {isMonitoredField
                                                                ? `(${formattedValue}${isRateFieldResult ? '%' : ''})`
                                                                : ''}
                                                        </Typography>
                                                    ) : (
                                                        <TableSortLabel
                                                            active={!!findOrderBy}
                                                            direction={findOrderBy?.order || SortMode.ASC}
                                                            onClick={() => handleRequestSort(headCell.id)}
                                                            onContextMenu={(event) => {
                                                                handleContextMenu?.(event, headCell.id);
                                                            }}
                                                            sx={{
                                                                userSelect: 'text',
                                                                position: 'relative',
                                                                '& .MuiSvgIcon-root': {
                                                                    position: 'absolute',
                                                                    left: '100%',
                                                                    fontSize: 16,
                                                                    marginLeft: '1px',
                                                                    transform: !!findOrderBy
                                                                        ? findOrderBy?.order === SortMode.ASC
                                                                            ? 'rotate(180deg)'
                                                                            : 'rotate(360deg)'
                                                                        : 'rotate(315deg)',
                                                                },
                                                            }}
                                                        >
                                                            <Typography
                                                                fontSize={14}
                                                                fontWeight={'medium'}
                                                                lineHeight={1}
                                                                whiteSpace={'pre-wrap'}
                                                            >
                                                                <Badge
                                                                    badgeContent={
                                                                        isMultiSort && findIndexOrderBy > -1
                                                                            ? findIndexOrderBy + 1
                                                                            : null
                                                                    }
                                                                    sx={{
                                                                        '& .MuiBadge-badge': {
                                                                            border: `2px solid #FFFFFF`,
                                                                            padding: '0 4px',
                                                                            fontSize: 9,
                                                                            top: -8,
                                                                            backgroundColor: color.grey400,
                                                                        },
                                                                    }}
                                                                >
                                                                    {headCell.label}
                                                                    {'\n'}
                                                                    {isMonitoredField
                                                                        ? `(${formattedValue}${
                                                                              isRateFieldResult ? '%' : ''
                                                                          })`
                                                                        : ''}
                                                                </Badge>
                                                            </Typography>
                                                        </TableSortLabel>
                                                    )}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                    )}

                    {loading ? (
                        <TableBody>
                            {[...Array(10)].map((item, index) => {
                                return (
                                    <TableRow key={`${index}+1`}>
                                        {tableHeaders.map((tableHead, index) => {
                                            return (
                                                <CellOfRow
                                                    value={<SkeletonRow />}
                                                    align={index === 0 ? 'left' : 'center'}
                                                    sx={getColumnStyles(
                                                        tableHead?.id,
                                                        tableDataTypes,
                                                        stickyLeftFields,
                                                        stickyRightFields
                                                    )}
                                                />
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    ) : (
                        <TableBody>
                            {data.map((rowData, index) => (
                                <TableRow key={index} sx={{ position: 'relative' }}>
                                    {selectedCamera && (
                                        <TableCell
                                            sx={{
                                                backgroundColor: color.grey100,
                                                ...cellSelectedSxProps,
                                            }}
                                        >
                                            <BaseCheckbox
                                                onChange={(e, checked) => {
                                                    selectedCamera?.handleSelected(
                                                        checked,
                                                        rowData[selectedCamera?.keySelected!] as string
                                                    );
                                                }}
                                                checked={selectedCamera?.macCameras.some(
                                                    (e) => e === rowData[selectedCamera?.keySelected!]
                                                )}
                                            />
                                        </TableCell>
                                    )}

                                    {tableHeaders.map((columnId, columnIndex) => (
                                        <TableCell
                                            key={columnIndex}
                                            align={getColumnAlignment(
                                                columnId.id,
                                                alignmentDataFields,
                                                stickyLeftFields
                                            )}
                                            sx={getColumnStyles(
                                                columnId.id,
                                                tableDataTypes,
                                                stickyLeftFields,
                                                stickyRightFields,
                                                !!selectedCamera
                                            )}
                                        >
                                            {renderColumnData(columnId.id, rowData, index)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    )}
                </Table>
            </ScrollContainer>

            {!data?.length && !loading && <>{noDataComponent}</>}
        </TableContainer>
    );
};

export const boxStyles: SxProps<Theme> = {
    maxWidth: '100%',
    padding: '4px 6px !important',
    border: 'none !important',
};

export const typographyStyles: SxProps<Theme> = {
    fontSize: 13,
};

export const tableHeadStyles = ({ style }: { style?: CSSProperties }): CSSProperties => ({
    position: 'sticky',
    top: 0,
    zIndex: 5,
    height: style?.height ?? 75,
});

const cellSelectedSxProps: SxProps<Theme> = {
    position: { md: 'sticky' },
    zIndex: 2,
    left: 0,
    width: 50,
};
